import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Button } from '@mui/material';
import { CheckCircle, RocketLaunch, Groups, Handshake } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import MGFintechShowcase from '../components/MGFintechShowcase';
import SEO from '../components/SEO';

const AboutUs = () => {
    const features = [
        {
            icon: <RocketLaunch sx={{ fontSize: 40, color: '#e3662d' }} />,
            title: 'Our Mission',
            description: 'To empower local businesses with cutting-edge technology, enabling them to compete in the digital age while fostering community connections.'
        },
        {
            icon: <Groups sx={{ fontSize: 40, color: '#5a9b9a' }} />,
            title: 'Our Vision',
            description: 'To become the leading platform for hyperlocal commerce, creating a sustainable ecosystem where businesses and customers thrive together.'
        },
        {
            icon: <Handshake sx={{ fontSize: 40, color: '#e3662d' }} />,
            title: 'Our Values',
            description: 'We believe in transparency, innovation, and community. Our success is measured by the success of our partners and the satisfaction of our users.'
        }
    ];

    return (
        <Box>
            <SEO
                title="About Us"
                description="Learn about 10D Stores, our mission to empower local businesses, and our vision for a sustainable hyperlocal commerce ecosystem."
                canonical="https://10dstores.in/about"
            />
            {/* Hero Section */}
            <Box sx={{
                backgroundColor: '#1E293B',
                color: 'white',
                py: 12,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <Container maxWidth="md">
                    <Typography variant="h2" component="h1" sx={{ fontWeight: 800, mb: 3, color: '#ffffff', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                        About 10D Stores
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#ffffff', maxWidth: '800px', mx: 'auto', lineHeight: 1.6 }}>
                        Bridging the gap between local businesses and digital convenience. We are redefining how you shop, save, and connect with your community.
                    </Typography>
                </Container>
            </Box>

            {/* Mission, Vision, Values */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Grid container spacing={4}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Card sx={{ height: '100%', borderRadius: 4, transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-8px)' } }}>
                                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                    <Box sx={{ mb: 3, display: 'inline-flex', p: 2, borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.03)' }}>
                                        {feature.icon}
                                    </Box>
                                    <Typography variant="h5" component="h3" sx={{ fontWeight: 700, mb: 2 }}>
                                        {feature.title}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        {feature.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Story Section */}
            <Box sx={{ backgroundColor: '#f8fafc', py: 10 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography variant="overline" sx={{ color: '#e3662d', fontWeight: 700, letterSpacing: 1.5 }}>
                                OUR STORY
                            </Typography>
                            <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mb: 3, mt: 1 }}>
                                Building for the Community
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ color: '#475569', fontSize: '1.1rem' }}>
                                Founded with a simple idea: local businesses are the heartbeat of our communities, but they often struggle to keep up with the digital revolution. 10D Stores was born to change that.
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ color: '#475569', fontSize: '1.1rem' }}>
                                We built a platform that gives businesses the tools they need to manage their operations, connect with customers, and grow their brand—without the complexity or high costs of traditional solutions.
                            </Typography>
                            <Box sx={{ mt: 4 }}>
                                {['Hyperlocal Focus', 'Digital Transformation', 'Community First'].map((item) => (
                                    <Box key={item} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <CheckCircle sx={{ color: '#10b981', mr: 2 }} />
                                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                            {item}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    height: 500,
                                    width: '100%',
                                    backgroundColor: '#cbd5e1',
                                    borderRadius: 4,
                                    backgroundImage: 'url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80)',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                                }}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* India Presence Section */}
            <Box sx={{ backgroundColor: '#ffffff', py: 10 }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mb: 2, textAlign: 'center' }}>
                        Our Presence Across India
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#64748b', mb: 6, textAlign: 'center' }}>
                        10D Stores is actively serving customers and businesses in major metropolitan cities across India
                    </Typography>
                    <Grid container spacing={3}>
                        {[
                            { name: 'Mumbai', state: 'Maharashtra' },
                            { name: 'Delhi', state: 'Delhi NCR' },
                            { name: 'Bangalore', state: 'Karnataka' },
                            { name: 'Kolkata', state: 'West Bengal' },
                            { name: 'Chennai', state: 'Tamil Nadu' },
                            { name: 'Hyderabad', state: 'Telangana' },
                            { name: 'Pune', state: 'Maharashtra' },
                            { name: 'Ahmedabad', state: 'Gujarat' },
                        ].map((city, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Card sx={{ 
                                    height: '100%', 
                                    borderRadius: 3, 
                                    textAlign: 'center',
                                    p: 3,
                                    transition: 'all 0.3s ease',
                                    '&:hover': { 
                                        transform: 'translateY(-4px)',
                                        boxShadow: 6,
                                        backgroundColor: '#f8fafc'
                                    }
                                }}>
                                    <CheckCircle sx={{ fontSize: 40, color: '#10b981', mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                        {city.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {city.state}
                                    </Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* CTA Section */}
            <Box sx={{ py: 12, textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mb: 3 }}>
                        Ready to Join the Revolution?
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#64748b', mb: 5 }}>
                        Whether you're a business owner looking to grow or a customer seeking the best local deals, 10D Stores is for you.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            variant="contained"
                            size="large"
                            component={Link}
                            to="/customers"
                            sx={{
                                backgroundColor: '#e3662d',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                '&:hover': { backgroundColor: '#d55a1f' }
                            }}
                        >
                            Get Started
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            component={Link}
                            to="/contact"
                            sx={{
                                borderColor: '#e3662d',
                                color: '#e3662d',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                '&:hover': { borderColor: '#d55a1f', backgroundColor: 'rgba(227, 102, 45, 0.04)' }
                            }}
                        >
                            Contact Us
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* MG Fintech Showcase */}
            <MGFintechShowcase />
        </Box>
    );
};

export default AboutUs;
